// HSSNA Token 存储 (跨端)
// 规格: SPEC §4.1 API 客户端架构 / §7 安全合规 / NF-SEC-01 / H-SEC-09 / C-SEC-05 / PIPL §28
//
// Web 端: 默认使用 secureStorage (AES-GCM 加密 + localStorage)
// RN 端:  通过 adapter 注入 (AsyncStorage + Keychain, 经 createSecureStorage 包装)
//
// 安全增强 (NF-SEC-01):
//   - 默认 adapter 由 webStorageAdapter (明文) 升级为 secureStorage (AES-GCM 加密)
//   - 防止 localStorage 明文落盘被第三方脚本/扩展直接读取
//   - 完美方案应使用 WebAuthn / Hardware-backed key (后续增强)
//
// 向后兼容:
//   - StorageAdapter 接口不变 (同时支持同步与异步实现)
//   - webStorageAdapter 保留导出 (旧代码 / 测试 / 显式明文场景使用)
//   - new TokenStore(adapter?) 与 new TokenStore() 行为兼容 (默认参数升级)
//   - createTokenStore(options?) 新增工厂函数, 支持注入自定义 secure storage

import { secureStorage } from '../security/secure-storage.js';
import type { SecureStorageAdapter } from '../security/secure-storage.js';

/**
 * 存储适配器接口
 * 支持同步(localStorage)与异步(AsyncStorage/secureStorage)实现
 */
export interface StorageAdapter {
  getItem(key: string): string | null | Promise<string | null>;
  setItem(key: string, value: string): void | Promise<void>;
  removeItem(key: string): void | Promise<void>;
}

const ACCESS_TOKEN_KEY = 'hssna:access_token';
const REFRESH_TOKEN_KEY = 'hssna:refresh_token';

/**
 * Web localStorage 适配器 (明文, 仅供测试 / 显式明文场景)
 *
 * @deprecated 生产环境请使用 secureStorage (默认) 或 createSecureStorage() 自定义加密存储.
 *             此适配器仅在 SSR / 测试 / 旧代码迁移时使用.
 * 在非浏览器环境(window/localStorage 不存在)下为 no-op
 */
export const webStorageAdapter: StorageAdapter = {
  getItem: (key) => {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }
    return window.localStorage.getItem(key);
  },
  setItem: (key, value) => {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    window.localStorage.setItem(key, value);
  },
  removeItem: (key) => {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    window.localStorage.removeItem(key);
  },
};

/**
 * SecureStorageAdapter 与 StorageAdapter 结构兼容
 * (Promise 返回值是 StorageAdapter 联合类型的子集)
 * 此处仅做类型断言,运行时直接使用 secureStorage 实例.
 */
const defaultSecureAdapter: StorageAdapter = secureStorage as SecureStorageAdapter;

/** TokenStore 创建选项 */
export interface TokenStoreOptions {
  /** 自定义存储适配器 (默认: secureStorage 加密适配器) */
  readonly adapter?: StorageAdapter;
}

/**
 * Token 存储 — 管理 access_token / refresh_token 的持久化
 *
 * 默认使用 secureStorage (Web: AES-GCM 加密 + localStorage, RN: 注入适配器).
 * 如需使用明文 localStorage (不推荐), 显式传入 webStorageAdapter.
 *
 * @example
 * // Web 端 (默认 secureStorage 加密)
 * const store = new TokenStore();
 * await store.setTokens(accessToken, refreshToken);
 *
 * // 显式使用明文 localStorage (不推荐, 仅供旧代码迁移)
 * const plainStore = new TokenStore(webStorageAdapter);
 *
 * // RN 端 (注入 AsyncStorage + Keychain)
 * const rnStore = new TokenStore({
 *   getItem: (k) => SecureKeychain.getItem(k),
 *   setItem: (k, v) => SecureKeychain.setItem(k, v),
 *   removeItem: (k) => SecureKeychain.removeItem(k),
 * });
 *
 * // 通过工厂函数 (推荐)
 * const store = createTokenStore({ adapter: customAdapter });
 */
export class TokenStore {
  private readonly adapter: StorageAdapter;

  constructor(adapter: StorageAdapter = defaultSecureAdapter) {
    this.adapter = adapter;
  }

  /** 获取 access_token */
  async getAccessToken(): Promise<string | null> {
    return await this.adapter.getItem(ACCESS_TOKEN_KEY);
  }

  /** 获取 refresh_token */
  async getRefreshToken(): Promise<string | null> {
    return await this.adapter.getItem(REFRESH_TOKEN_KEY);
  }

  /** 同时设置 access_token 与 refresh_token */
  async setTokens(accessToken: string, refreshToken: string): Promise<void> {
    await this.adapter.setItem(ACCESS_TOKEN_KEY, accessToken);
    await this.adapter.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }

  /** 清除所有 token (登出/失效) */
  async clear(): Promise<void> {
    await this.adapter.removeItem(ACCESS_TOKEN_KEY);
    await this.adapter.removeItem(REFRESH_TOKEN_KEY);
  }
}

/**
 * 创建 TokenStore 工厂函数
 *
 * @param options 配置选项
 *   - adapter: 自定义存储适配器 (默认: secureStorage 加密适配器)
 *
 * @example
 * ```ts
 * // 默认 (secureStorage 加密)
 * const store = createTokenStore();
 *
 * // 自定义 secure storage (RN 端注入 Keychain)
 * const rnStore = createTokenStore({
 *   adapter: createSecureStorage(keychainAdapter),
 * });
 * ```
 */
export function createTokenStore(options?: TokenStoreOptions): TokenStore {
  return new TokenStore(options?.adapter ?? defaultSecureAdapter);
}
